import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { apiClient } from "@/api/apiClient";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getOptimizedImageUrl } from "@/utils";
import { useOnboarding } from "@/hooks/useOnboarding";

export function useSwipeEngine(initialFilters) {
    const { completeTask } = useOnboarding();
    const [filters, setFilters] = useState(initialFilters);
    const [filterOptions, setFilterOptions] = useState(null);

    // Restore currentIndex from localStorage
    const [currentIndex, setCurrentIndex] = useState(() => {
        const saved = localStorage.getItem('swipeCurrentIndex');
        return saved ? parseInt(saved, 10) : 0;
    });

    useEffect(() => {
        localStorage.setItem('swipeCurrentIndex', currentIndex.toString());
    }, [currentIndex]);

    // State for products and pagination (persisted in localStorage for mobile sleep recovery)
    const [allProducts, setAllProducts] = useState(() => {
        try {
            const saved = localStorage.getItem('swipeProducts');
            if (saved === 'undefined' || saved === 'null') return [];
            const parsed = saved ? JSON.parse(saved) : [];
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    });

    useEffect(() => {
        if (allProducts && allProducts.length > 0) {
            try {
                localStorage.setItem('swipeProducts', JSON.stringify(allProducts));
            } catch (e) {
                // Ignore quota errors
            }
        }
    }, [allProducts]);

    const [isLoading, setIsLoading] = useState(!allProducts || allProducts.length === 0);
    const [isReloading, setIsReloading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    
    const isFetchingRef = useRef(false);
    const filterDebounceRef = useRef(null);
    const prevFiltersRef = useRef(filters);
    const lastSwipeTimeRef = useRef(0);
    const processedIndicesRef = useRef(new Set());

    // Filter Change Detection
    useEffect(() => {
        const prev = prevFiltersRef.current;
        const isDifferent = JSON.stringify(prev) !== JSON.stringify(filters);

        if (isDifferent) {
            prevFiltersRef.current = filters;
            setCurrentIndex(0);
            setIsReloading(true);
            processedIndicesRef.current.clear();

            if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
            filterDebounceRef.current = setTimeout(() => {
                isFetchingRef.current = false;
                loadMoreProducts(true, true);
            }, 600);
        }

        return () => {
            if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
        };
    }, [filters]);

    const loadMoreProducts = async (reset = false, isFilterChange = false) => {
        if (isFetchingRef.current && !isFilterChange) return;
        isFetchingRef.current = true;
        if (reset) {
            if (isFilterChange) setIsReloading(true);
            else setIsLoading(true);
        }

        try {
            const limit = 50;
            const offset = reset ? 0 : (allProducts || []).length;
            const sortOrder = 'recommended';

            const fetchedProducts = await apiClient.entities.Product.list(sortOrder, {
                ...filters,
                limit,
                offset
            });

            setAllProducts(prev => {
                const processedProducts = fetchedProducts || [];
                if (reset) return processedProducts;

                const currentIds = new Set((prev || []).map(p => p.id));
                const uniqueNewProducts = processedProducts.filter(p => !currentIds.has(p.id));
                return [...prev, ...uniqueNewProducts];
            });

            if (reset) {
                if (fetchedProducts.totalCount !== undefined) {
                    setTotalCount(fetchedProducts.totalCount);
                } else {
                    const fallbackCount = fetchedProducts.length >= limit ? 999 : fetchedProducts.length;
                    setTotalCount(fallbackCount);
                }
            }
        } catch (err) {
            console.error("Fetch failed", err);
        } finally {
            isFetchingRef.current = false;
            setIsLoading(false);
            setIsReloading(false);
        }
    };

    // Initial Load
    useEffect(() => {
        apiClient.entities.Product.count().then(count => {
            setTotalCount(count);
            const localCount = (allProducts || []).length;
            const isStale = (localCount < 10 && count > 10) || (localCount === 0 && count > 0);

            if (isStale || (!allProducts || allProducts.length === 0)) {
                loadMoreProducts(true);
            } else {
                setIsLoading(false);
            }
        });

        apiClient.entities.Product.getMetadata().then(meta => {
            setFilterOptions(meta);
        });
    }, []);

    // Infinite Scroll Trigger
    useEffect(() => {
        if (allProducts && allProducts.length > 0 && currentIndex > allProducts.length - 5) {
            loadMoreProducts();
        }
    }, [currentIndex, allProducts?.length]);

    // Interactions
    const { data: interactions = [] } = useQuery({
        queryKey: ['interactions'],
        queryFn: async () => {
            try { await apiClient.auth.me(); } catch (e) {}
            return apiClient.entities.UserInteraction.list();
        },
    });

    const createInteraction = useMutation({
        mutationFn: (data) => apiClient.entities.UserInteraction.create(data),
    });

    const viewedProductIds = useMemo(() => interactions
        .filter(i => i.action !== 'view')
        .map(i => i.product_id), [interactions]);

    const hadInteractionsRef = useRef(false);

    useEffect(() => {
        if (viewedProductIds.length > 0) {
            hadInteractionsRef.current = true;
        } else if (hadInteractionsRef.current) {
            setCurrentIndex(0);
            hadInteractionsRef.current = false;
        }
    }, [viewedProductIds]);

    const filteredProducts = useMemo(() => allProducts, [allProducts]);
    const currentProduct = filteredProducts[currentIndex];

    // Preload images
    useEffect(() => {
        if (currentProduct?.images) {
            currentProduct.images.forEach(imageUrl => {
                if (!imageUrl) return;
                const img = new Image();
                img.src = getOptimizedImageUrl(imageUrl, 600);
            });
        }

        const preloadCardCount = 3;
        for (let i = 1; i <= preloadCardCount; i++) {
            const nextProduct = filteredProducts[currentIndex + i];
            if (nextProduct?.images?.[0]) {
                const img = new Image();
                img.src = getOptimizedImageUrl(nextProduct.images[0], 600);
            }
        }
    }, [currentIndex, filteredProducts, currentProduct]);

    // View Duration Tracking
    const viewStartTimeRef = useRef(Date.now());
    useEffect(() => {
        viewStartTimeRef.current = Date.now();
        const activeProductId = currentProduct?.id;

        return () => {
            if (activeProductId) {
                const duration = (Date.now() - viewStartTimeRef.current) / 1000;
                if (duration > 0.1 && duration < 300) {
                    apiClient.entities.UserInteraction.create({
                        product_id: activeProductId,
                        action: 'view_produit',
                        duration_seconds: Math.min(Math.max(1, Math.round(duration)), 60),
                    });
                }
            }
        };
    }, [currentProduct?.id]);

    const handleSwipe = (direction, metadata = {}) => {
        const now = Date.now();
        if (now - lastSwipeTimeRef.current < 500) return;
        if (processedIndicesRef.current.has(currentIndex)) return;

        lastSwipeTimeRef.current = now;
        processedIndicesRef.current.add(currentIndex);

        if (!currentProduct) return;

        completeTask('first_swipe');
        if (direction === "right") completeTask('like_item');

        createInteraction.mutate({
            product_id: currentProduct.id,
            action: direction === "right" ? "swipe_right" : "swipe_left",
            context: "swipe_mode",
            product: currentProduct,
            metadata
        });

        setTimeout(() => {
            setCurrentIndex(prev => prev + 1);
        }, 300);
    };

    const handleUndo = () => {
        if (currentIndex > 0) setCurrentIndex(prev => prev - 1);
    };

    const handleExpandDescription = useCallback((productId) => {
        apiClient.entities.UserInteraction.create({
            product_id: productId,
            action: 'expand_description'
        });
    }, []);

    const handleVisitSite = useCallback((productId) => {
        apiClient.entities.UserInteraction.create({
            product_id: productId,
            action: 'visit_marque_site'
        });
    }, []);

    const handleVariantClick = useCallback((variant) => {
        const variantIndex = filteredProducts.findIndex(p => p.id === variant.id);
        if (variantIndex !== -1) {
            setCurrentIndex(variantIndex);
        } else {
            setAllProducts(prev => {
                const currentId = filteredProducts[currentIndex]?.id;
                if (!currentId) return [...prev, variant];

                const idxInAll = prev.findIndex(p => p.id === currentId);
                if (idxInAll === -1) return [...prev, variant];

                const newList = [...prev];
                const existingIdx = prev.findIndex(p => p.id === variant.id);
                if (existingIdx !== -1) return prev;

                newList.splice(idxInAll + 1, 0, variant);
                return newList;
            });
        }
    }, [filteredProducts, currentIndex]);

    const handleResetFilters = useCallback(() => {
        setFilters(initialFilters);
        setCurrentIndex(0);
        setAllProducts([]);
        localStorage.removeItem('swipeProducts');
        localStorage.setItem('swipeCurrentIndex', '0');
        loadMoreProducts(true);
    }, [initialFilters]);

    return {
        products: filteredProducts,
        currentProduct,
        currentIndex,
        totalCount,
        isLoading,
        isReloading,
        filters,
        filterOptions,
        setFilters,
        handleSwipe,
        handleUndo,
        handleExpandDescription,
        handleVisitSite,
        handleVariantClick,
        handleResetFilters,
    };
}
